import React, { useCallback, useMemo, useRef, useState } from 'react';
import { getLimitWarning } from 'utils';

import { constants, IntlManager, NotificationManager, useApplicationState } from '@atlas/core';
import { Button, Icon, Label } from '@atlas/ui';

import FormMobile from 'common/Form/FormMobile';
import useTranslate from 'common/I18n/Interpreter';
import {
  onSetDepositLimitsSubmit,
  onSetLostLimitsSubmit,
} from 'src/common/Form/formConfig/responsibleGaming';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';

import LimitInput from './LimitInput';

const { DEPOSIT, GAME_SPENT, SPENT, SPORT_SPENT } = constants.SELF_REGULATION_LIMIT;

const errorMessages = {
  [DEPOSIT]: 'limit_error_deposit_message',
  [GAME_SPENT]: 'limit_error_casino_message',
  [SPENT]: 'limit_error_common_message',
  [SPORT_SPENT]: 'limit_error_sport_message',
};

const MIN_VALUE = 100; // 1.00

const LimitsEdit = ({
  checkCommonSpentLimit,
  checkNotCommonSpentLimit,
  limitType,
  regulationLimit,
  setRegulationLimit,
  toggleEdit,
}) => {
  const t = useTranslate();
  const { account } = useApplicationState();
  const { markSpentLimitAsSet } = useSelfRegulationContext();
  const formRef = useRef(null);
  const [disabledSaveButtons, setDisabledSaveButtons] = useState({});
  const isSpentLimitRequired = useMemo(
    () => limitType === SPENT && account.spentLimitConfigurationEnabled,
    [limitType, account.spentLimitConfigurationEnabled],
  );

  const formFields = useMemo(() => {
    const initialValues = {};
    const fields = regulationLimit.map(
      ({ defaultValue, error = false, future, type: name, value }) => ({
        component: props => (
          <LimitInput
            {...props}
            onChange={(value, touched) => {
              if (isSpentLimitRequired) {
                const safeValue = value === null || value === undefined ? '' : String(value);
                const cleanValue = safeValue.replace(/^0+(?=\d)/, '');
                const numValue = Number(cleanValue);
                if (cleanValue === '' || Number.isNaN(numValue)) {
                  setDisabledSaveButtons(prevState => ({
                    ...prevState,
                    [name]: true,
                  }));
                } else {
                  setDisabledSaveButtons(prevState => ({
                    ...prevState,
                    [name]: false,
                  }));
                }

                const finalValue = numValue < 1 ? MIN_VALUE : cleanValue;

                props.onChange(finalValue, touched);
              } else {
                props.onChange(value, touched);
              }
            }}
            required={isSpentLimitRequired}
          />
        ),
        error,
        errorMessage: errorMessages[limitType],
        formRef,
        name,
        rules: [],
        value: isSpentLimitRequired ? (value ?? defaultValue ?? MIN_VALUE) : value,
        warning: getLimitWarning(future, name),
      }),
    );

    return {
      config: { initialValues },
      fields,
      name: 'selfRegulationLimits',
    };
  }, [regulationLimit, limitType, isSpentLimitRequired]);

  const action = useCallback(
    async (formData, actions) => {
      let isError;

      if (limitType === SPENT) {
        isError = checkCommonSpentLimit(formData);
      } else {
        isError = checkNotCommonSpentLimit(formData, limitType);
      }

      if (isError) return;

      return (limitType === DEPOSIT ? onSetDepositLimitsSubmit : onSetLostLimitsSubmit)(
        formData,
        actions,
        limitType,
      );
    },
    [limitType, checkCommonSpentLimit, checkNotCommonSpentLimit],
  );

  const submitCallback = useCallback(
    data => {
      if (data) {
        const notificationText = data.reduce((acc, text) => {
          const periodLimit = regulationLimit.find(period => period.type === text.type);
          if (text.value && (text.value < periodLimit.value || !periodLimit.value)) {
            const processedValue = `${IntlManager.formatMoney(text.value)}`;
            acc += `, ${t(`responsible_gaming_${text.type}`)} - ${processedValue}`;
          }

          return acc;
        }, '');

        if (notificationText.length) {
          NotificationManager.notify({
            data: {
              type: t(`${limitType.toLowerCase()}_limits`),
              value: `${notificationText.slice(2)} ${t('limits_was_activated')}`,
            },
            message: 'limits_were_edited',
            status: 'success',
            type: 'notification',
          });
        }

        setRegulationLimit(data, limitType);
        markSpentLimitAsSet();
        toggleEdit();
      }
    },
    [regulationLimit, setRegulationLimit, toggleEdit, t, limitType, markSpentLimitAsSet],
  );

  const handleCancel = useCallback(() => {
    setRegulationLimit(regulationLimit, limitType);
    toggleEdit();
  }, [setRegulationLimit, regulationLimit, limitType, toggleEdit]);

  const cancelButton = useMemo(
    () => <Button label="cancel" onClick={handleCancel} size="big" type="secondary" />,
    [handleCancel],
  );

  const isSaveButtonDisabled =
    isSpentLimitRequired && Object.values(disabledSaveButtons).some(v => v);

  const zeroLimitBlock = useMemo(
    () => (
      <>
        {limitType !== SPENT ? (
          <div className="limit__zero-block">
            <Icon height={24} name="self-regulation-info" />
            <Label message="self_reg_zero_limit" />
          </div>
        ) : (
          <>
            {isSaveButtonDisabled && (
              <div className="limit__zero-block limit__zero-block--error">
                <Icon height={24} name="self-regulation-info" />
                <Label message="common_limit_please_fill_message" />
              </div>
            )}
          </>
        )}
      </>
    ),
    [isSaveButtonDisabled, limitType],
  );

  return (
    <FormMobile
      action={action}
      buttonText="save"
      centerContent={zeroLimitBlock}
      className="self-regulation__form"
      disableButton={isSaveButtonDisabled}
      disablePristine={!isSpentLimitRequired}
      form={formFields}
      leftSideSubmitContent={cancelButton}
      onSubmit={submitCallback}
      ref={formRef}
    />
  );
};

export default LimitsEdit;
